import {Clipboard} from '@angular/cdk/clipboard';
import {AfterViewInit, Component, Injector, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, ValidationErrors} from '@angular/forms';
import {MatSidenav} from "@angular/material/sidenav";
import {MatTableDataSource} from '@angular/material/table';
import {Router} from "@angular/router";
import {BasePageComponent} from '@core-components/page-content/base-page.component';
import {LandlordsService, SUPPORTED_OFFERING_TYPES} from "@core-services/landlords.service";
import {TabManagementService} from '@core-services/tab-management.service';
import {PageItemModel} from '@core/model/page-item.model';
import {ApplicationForm} from '@core/model/retailer/application-form.model';
import {ConsoleContextService} from "@security/console-context.service";
import {DateUtilsService} from '@shared-services/dateUtils.service';
import {UtilsService} from '@shared-services/utils.service';
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {CustomValidators} from '@ui-kit/framework/util/validators/custom-validators';
import {SnackbarService} from "projects/nter-ui-kit/src/lib/framework/util/service/snackbar.service";
import {equalsIgnoreCase, isNotEmpty} from "projects/nter-ui-kit/src/lib/framework/util/static/string-util";
import QRCode from 'qrcode';
import {lastValueFrom} from 'rxjs';
import {LocationOffering} from "src/app/core/model/side-nav.model";
import {environment} from 'src/environments/environment';
import {ApplicationFormsService} from '../application-forms.service';
import {FormStepperComponent} from '../form-stepper/form-stepper.component';

@Component({
  selector: 'app-manage-application-form-builder',
  templateUrl: './manage-application-forms.component.html',
  styleUrls: ['./manage-application-forms.component.scss']
})
export class ManageApplicationFormsComponent extends BasePageComponent implements AfterViewInit {
  @ViewChild('rightSidenav') sidenav!: MatSidenav;

  displayedColumns: string[] = ['formName', 'isCurrentlyActive', 'updateDate', 'preview', 'makeActive', 'edit', 'duplicate', 'delete'];
  private applicationFormTemplates: ApplicationForm[] = [];
  dataSource = new MatTableDataSource(this.applicationFormTemplates);

  universalAppLink: string;
  qrCodeImageSrc?: string;
  qrCodeUrl: string | null = null;

  // CS-1612: where the embedded application form sends applicants after a successful submission
  readonly redirectUrl = new FormControl<string | null>(null, [CustomValidators.websiteUrl, redirectUrlProtocol]);

  constructor(injector: Injector,
              public loader: LoadingService,
              public dateUtils: DateUtilsService,
              public utils: UtilsService,
              private applicationFormsService: ApplicationFormsService,
              private tabManagementService: TabManagementService,
              private clipboard: Clipboard,
              private currentContext: ConsoleContextService,
              private landlordService: LandlordsService,
              private router: Router,
              private snackbarService: SnackbarService) {

    super(injector);
    this.loadAllTemplates();
    this.universalAppLink = this.buildUniversalAppLink(); //removed in master
    this.generateQRCode();

    this.tabManagementService.getPageInFocus().subscribe((page: PageItemModel | null) => {
      if (page && page.id == this.pageId.id) {
        this.loadAllTemplates();
      }
    });
  }

  ngAfterViewInit(): void {
    this.qrCodeUrl = this.getApplicationFormQrCodeDestination();
    this.redirectUrl.setValue(this.redirectUrlFromContext);
    setTimeout(() => this.sidenav.open(), 200);
  }

  deleteFormTemplate(element: ApplicationForm): void {
    lastValueFrom(this.applicationFormsService.deleteOneFormTemplate(element.uuid!))
      .then(() => {
        this.loadAllTemplates();
      })
      .catch((error: string) => {
        console.log(error);
      });
  }

  setAsActiveFormTemplate(element: ApplicationForm): void {
    lastValueFrom(this.applicationFormsService.setOneFormTemplateAsActive(element.uuid!))
      .then((res: ApplicationForm[]) => {
        this.applicationFormTemplates = res;
        this.dataSource.data = res;
      })
      .catch((error: string) => {
        console.log(error);
      });
  }

  public previewForm(element: ApplicationForm) {
    const locationOffering = this.currentContext.getCurrentLocation()?.locationOfferings?.find(offering => offering.offeringUuid === this.offering?.offeringUuid)!;

    const url = this.router.serializeUrl(this.router.createUrlTree([`/preview-apply/${element.formCode}`],
      {
        queryParams: {
          locationCode: locationOffering.locationCode!,
          offeringCode: locationOffering.offeringCode!,
          embedKey: locationOffering.embedKey!
        }
      }));
    window.open(url, '_blank');
  }

  editExistingApplication(element: ApplicationForm) {
    this.tabManagementService.sendPageAddEvent({
      componentToRegister: FormStepperComponent,
      data: element,
      pageName: 'Edit Application Form',
      pageCode: 'edit_application_form',
      sectionCode: 'settings',
      offering: this.offering,
      pageHeader: 'Edit Application Form',
      id: this.utils.generateUuidWithPrefix('edit_application_form')
    });
  }

  startNewApplication(): void {
    this.tabManagementService.sendPageAddEvent({
      componentToRegister: FormStepperComponent,
      data: null,
      pageName: 'New Application Form',
      pageCode: 'new_application_form',
      sectionCode: 'settings',
      offering: this.offering,
      pageHeader: 'New Application Form',
      id: this.utils.generateUuidWithPrefix('new_application_form')
    });
  }

  duplicateForm(element: ApplicationForm): void {
    lastValueFrom(this.applicationFormsService.duplicateForm(element.uuid))
      .then(() => {
        this.loadAllTemplates();
      })
      .catch((error: string) => {
        console.log(error);
      });
  }

  private loadAllTemplates(): void {
    lastValueFrom(this.applicationFormsService.getAllFormTemplatesForCurrentOffering())
      .then((res: ApplicationForm[]) => {
        this.applicationFormTemplates = res;
        this.dataSource.data = res;
      })
      .catch((error: string) => {
        console.log(error);
      });
  }

  canBeUpdated(element: ApplicationForm): boolean {
    if (element.formStatus) {
      return element.formStatus?.toLowerCase() !== 'used';
    }
    return true;
  }

  private buildUniversalAppLink(): string {
    const locationOffering = this.currentContext.getCurrentLocation()?.locationOfferings?.find((offering: any) => offering.offeringUuid === this.offering?.offeringUuid)!;
    return `${environment.apiPortal}/${locationOffering.locationCode}/${locationOffering.offeringCode}/apply?embedKey=${locationOffering.embedKey}`;
  }

  copyEmbedCode() {
    const locationOffering = this.currentContext.getCurrentLocation()?.locationOfferings
      ?.find(offering => offering.offeringUuid === this.offering?.offeringUuid)!;

    const script: HTMLScriptElement = document.createElement('script');
    script.type = 'text/javascript';
    script.src = environment.apiAppForm;

    const formEl: HTMLElement = document.createElement('nter-app-form');
    formEl.setAttribute('location-code', locationOffering.locationCode);
    formEl.setAttribute('offering-code', locationOffering.offeringCode!);
    formEl.setAttribute('embed-key', locationOffering.embedKey!);

    const stringToCopy = script.outerHTML + '\n' + formEl.outerHTML;
    if (this.clipboard.copy(stringToCopy)) {
      this.snackbarService.openSnackBar('Embed code copied to clipboard!');
      return;
    }

    //if the clipboard.copy fails, open a modal with the above html code in a textarea for manual copying
    this.snackbarService.openSnackBar(stringToCopy);
  }

  copyHostedFormLink() {
    if (this.clipboard.copy(this.universalAppLink)) {
      this.snackbarService.openSnackBar('Hosted form link copied to clipboard!');
      return;
    }

    //if the clipboard.copy fails, open a modal with the above html code in a textarea for manual copying
    this.snackbarService.openSnackBar(this.universalAppLink);
  }

  private generateQRCode() {
    QRCode.toDataURL(this.universalAppLink)
      .then(url => {
        this.qrCodeImageSrc = url;
      })
      .catch(err => {
        console.error(err);
        this.snackbarService.openSnackBar('Could not generate QR code!');
      })
  }

  downloadQRCodeImage() {
    if (this.qrCodeImageSrc) {
      const link = document.createElement('a');
      link.download = 'qr-code.png';
      link.href = this.qrCodeImageSrc;
      link.click();
    } else {
      console.error('QR code image source is undefined');
      this.snackbarService.openSnackBar('QR image could not be downloaded');
      return;
    }
  }

  private getApplicationFormQrCodeDestination(): string | null {
    const offering = this.currentContext.getCurrentLocation()?.locationOfferings
      ?.find((offering: any) => offering.offeringUuid === this.offering?.offeringUuid)!;
    return offering.applicationFormQrCodeDestination ?? null;
  }

  get qrCodeUrlFromContext(): string | null {
    return this.getApplicationFormQrCodeDestination();
  }

  get canUpdateCustomUQrUrl(): boolean {
    return isNotEmpty(this.qrCodeUrl) && !equalsIgnoreCase(this.qrCodeUrl, this.qrCodeUrlFromContext);
  }

  protected testExternalUrl(): void {
    if (this.qrCodeUrl) {
      //sanitize url for safety before opening and parse string as valid url
      const url = new URL(this.qrCodeUrl);
      window.open(url, '_blank');
    } else {
      this.utils.openSnackBar("No valid url to test. Please update the url first.")
    }
  }

  protected updateQrCodeUrl(): void {
    const currentOffering = this.currentContext.getCurrentLocationOffering(this.offering!.uuid);
    lastValueFrom(this.landlordService.updateQrCodeUrl(this.qrCodeUrl, currentOffering!))
      .then(() => {
        // return lastValueFrom(this.landlordService.getOfferingsForLocation(this.currentContext.getCurrentLocation()!.uuid, SUPPORTED_OFFERING_TYPES))
        return Promise.resolve(lastValueFrom(this.landlordService.getOfferingsForLocation(this.currentContext.getCurrentLocation()!.uuid, SUPPORTED_OFFERING_TYPES)));
      })
      .then((res: LocationOffering[]) => {
        const updatedOffering = res.find(offering => offering.offeringUuid === this.offering!.offeringUuid);
        this.currentContext.updateLocationOfferings(updatedOffering!);
        this.utils.openSnackBar("QR code url updated successfully");
      });
  }

  protected removeCustomUrl(): void {
    this.qrCodeUrl = null;
    this.updateQrCodeUrl();
  }

  get redirectUrlFromContext(): string | null {
    const offering = this.currentContext.getCurrentLocation()?.locationOfferings
      ?.find((offering: any) => offering.offeringUuid === this.offering?.offeringUuid);
    return offering?.applicationFormRedirectEnabled ? offering.applicationFormRedirectUrl ?? null : null;
  }

  get canUpdateApplicationFormRedirect(): boolean {
    const url = this.redirectUrl.value?.trim();
    return !!url && this.redirectUrl.valid && url !== this.redirectUrlFromContext;
  }

  get redirectUrlError(): string | null {
    const errors = this.redirectUrl.errors;
    if (errors?.['websiteUrlInvalid'] || errors?.['redirectUrlProtocol']) {
      return 'Enter a full URL starting with https:// or http://';
    }
    return null;
  }

  protected updateApplicationFormRedirect(): void {
    this.saveApplicationFormRedirect(this.redirectUrl.value?.trim() || null, "Redirect link updated successfully");
  }

  protected removeApplicationFormRedirect(): void {
    this.redirectUrl.setValue(null);
    this.saveApplicationFormRedirect(null, "Redirect link removed");
  }

  private saveApplicationFormRedirect(url: string | null, successMessage: string): void {
    const currentOffering = this.currentContext.getCurrentLocationOffering(this.offering!.uuid);
    lastValueFrom(this.landlordService.updateApplicationFormRedirect({enabled: url !== null, url}, currentOffering!))
      .then(() => lastValueFrom(this.landlordService.getOfferingsForLocation(this.currentContext.getCurrentLocation()!.uuid, SUPPORTED_OFFERING_TYPES)))
      .then((res: LocationOffering[]) => {
        const updatedOffering = res.find(offering => offering.offeringUuid === this.offering!.offeringUuid);
        this.currentContext.updateLocationOfferings(updatedOffering!);
        this.utils.openSnackBar(successMessage);
      })
      .catch(() => this.utils.openSnackBar("Could not update the redirect link"));
  }
}

function redirectUrlProtocol(control: AbstractControl): ValidationErrors | null {
  return control.value && !/^https?:\/\//i.test(control.value) ? {redirectUrlProtocol: true} : null;
}
